import { Wizard } from "react-use-wizard";
import { useRef, useState } from "react";
import { TKeysOfType } from "../../../../shared/types";
import {
  EPurchasePurpose,
  EDealRequestCommunicationType,
  EDealRequestTimeInterval,
  EDealRequestViewType,
} from "../../../../shared/wizard/steps/types";

import getBaseSteps from "./steps/stepsArray/getBaseSteps";
import getShureSteps from "./steps/stepsArray/getShureSteps";
import getShureBuySteps from "./steps/stepsArray/getShureBuySteps";
import getShureSwapSteps from "./steps/stepsArray/getShureSwapSteps";
import getWishSteps from "./steps/stepsArray/getWishSteps";
import getWishBuySteps from "./steps/stepsArray/getWishBuySteps";
import getSelectionByOwnAppSteps from "./steps/stepsArray/getSelectionByOwnAppSteps";
import getWishSwapSteps from "./steps/stepsArray/getWishSwapSteps";
import getSelectionByOwnAppSwapSteps from "./steps/stepsArray/getSelectionByOwnAppSwapSteps";
import getSelectionByOwnAppBuySteps from "./steps/stepsArray/getSelectionByOwnAppBuySteps";

import clsx from "clsx";
import styles from "./styles.module.scss";
import { IDataFeatures } from "../../../../shared/wizard/steps/featuresStep";
import AnimatedStep from "../../../../shared/wizard/steps/animatedStep";
export interface IFillDataWizardBuy extends IDataFeatures {
  freeComment: string | null;

  complexId: number | null;
  streetId: number | null;
  buildingId: number | null;
  areaInSqM: number | null;
  apartmentNumber: number | null;
  facilityTypeId: number | null;
  estimatedPrice: number | null;

  communicationType: EDealRequestCommunicationType | null;
  timeIntervalTypeCommunication: EDealRequestTimeInterval | null;
  timeIntervalTypeView: EDealRequestTimeInterval | null;
  viewRealtorParticipation: EDealRequestViewType | null;

  finance: null | boolean;
  aim: null | EPurchasePurpose;
  selectedFacilityTypeIds: number[] | null;
  selectedFacilityTypeOwnFacility: number | null;

  desiredLocationIds: number[] | null;
  rejectedLocationIds: number[] | null;
}

export type TStringKeysOfIFillDataWizardBuy = TKeysOfType<
  IFillDataWizardBuy,
  string | null
>;

export type TNumberKeysOfIFillDataWizardBuy = TKeysOfType<
  IFillDataWizardBuy,
  number | null | number[]
>;

export type TBooleanKeysOfIFillDataWizardBuy = TKeysOfType<
  IFillDataWizardBuy,
  boolean | null
>;

export type TKeyOfDataWizardBuy = keyof IFillDataWizardBuy;

const IS_PADDING_NARROW = window.innerWidth < 600;

const INITIAL_FILL_DATA_WIZARD = {
  freeComment: null,

  complexId: null,
  streetId: null,
  buildingId: null,
  areaInSqM: null,
  apartmentNumber: null,
  facilityTypeId: null,
  estimatedPrice: null,

  communicationType: null,
  timeIntervalTypeView: null,
  timeIntervalTypeCommunication: null,
  viewRealtorParticipation: null,

  encumbrance: null,
  childrenOwn: null,
  privatization: null,
  arrest: null,
  debt: null,

  finance: null,
  aim: null,
  selectedFacilityTypeIds: null,
  selectedFacilityTypeOwnFacility: null,

  desiredLocationIds: null,
  rejectedLocationIds: null,
};

// https://sboard.online/boards/8c2808b6-6787-4dd3-bf8e-f6f9666a8374 смотри схему в сборде. Ветвления разложены по папкам.

const BuyWizard = () => {
  const [fillDataWizard, setFillDataWizard] = useState<IFillDataWizardBuy>(
    INITIAL_FILL_DATA_WIZARD
  );

  const handleChangeDataString = (
    data: string,
    key: TStringKeysOfIFillDataWizardBuy
  ) => {
    setFillDataWizard((prev) => {
      return { ...prev, [key]: data };
    });
  };

  const handleChangeDataNumber = (
    data: number | number[],
    key: TNumberKeysOfIFillDataWizardBuy
  ) => {
    setFillDataWizard((prev) => {
      return { ...prev, [key]: data };
    });
  };

  const handleChangeDataObject = (data: Partial<IFillDataWizardBuy>) => {
    setFillDataWizard((prev) => {
      return { ...prev, ...data };
    });
  };

  const handleChangeDataBoolean = (
    data: boolean,
    key: TBooleanKeysOfIFillDataWizardBuy
  ) => {
    setFillDataWizard((prev) => {
      return { ...prev, [key]: data };
    });
  };

  const handleResetData = (keys: TKeyOfDataWizardBuy[], data?: number[]) => {
    if (keys.length === 1) {
      const key = keys[0];
      setFillDataWizard((prev) => {
        return { ...prev, [key]: data ? data : null };
      });
    } else {
      const resetValues = keys.reduce((acc, key) => {
        acc[key] = null;
        return acc;
      }, {} as Partial<IFillDataWizardBuy>);
      setFillDataWizard((prevData) => ({ ...prevData, ...resetValues }));
    }
  };

  const isNextStep_estimateWithoutApartment =
    fillDataWizard.areaInSqM &&
    fillDataWizard.buildingId &&
    fillDataWizard.streetId;

  const isNextStep_estimateWithoutApartment_b = !Boolean(
    isNextStep_estimateWithoutApartment
  );

  const isNextStep_estimate = !Boolean(
    isNextStep_estimateWithoutApartment && fillDataWizard.apartmentNumber
  );
  const isNextStep_communicationType = !Boolean(
    fillDataWizard.communicationType
  );
  const isNextStep_timeIntervalTypeCommunication = !Boolean(
    fillDataWizard.timeIntervalTypeCommunication
  );
  const isNextStep_viewRealtorParticipation = !Boolean(
    fillDataWizard.viewRealtorParticipation
  );
  const isNextStep_timeIntervalTypeView = !Boolean(
    fillDataWizard.timeIntervalTypeView
  );
  const dataFeatures: IDataFeatures = {
    encumbrance: fillDataWizard.encumbrance,
    childrenOwn: fillDataWizard.childrenOwn,
    privatization: fillDataWizard.privatization,
    arrest: fillDataWizard.arrest,
    debt: fillDataWizard.debt,
  };
  const dataFormEstimate = {
    streetId: fillDataWizard.streetId,
    buildingId: fillDataWizard.buildingId,
    areaInSqM: fillDataWizard.areaInSqM,
    apartmentNumber: fillDataWizard.apartmentNumber,
  };
  const isNextStep_dataFeatures = Object.values(dataFeatures).some(
    (value) => value === null
  );
  const isTriplePrevSteps = !Boolean(
    fillDataWizard.selectedFacilityTypeIds?.length === 2
  );
  const isDoublePrevSteps = !Boolean(fillDataWizard.rejectedLocationIds?.length);
  const isNextStepDisabled_selectedPolygonIds = !Boolean(
    fillDataWizard.desiredLocationIds?.length
  );
  const isNextStepDisabled_finance = !Boolean(fillDataWizard.finance !== null);
  const isNextStepDisabled_aim = !Boolean(fillDataWizard.aim);
  const isNextStepDisabled_selectedTypeIds = !Boolean(
    fillDataWizard.selectedFacilityTypeIds?.length
  );
  const isNextStepDisabled_selectedFacilityTypeOwnFacility = !Boolean(
    fillDataWizard.selectedFacilityTypeOwnFacility
  );
  const isNextStepDisabled_apartmentNumber = !Boolean(
    fillDataWizard.apartmentNumber
  );

  const base = getBaseSteps();

  const shureSteps = getShureSteps(
    fillDataWizard,
    handleChangeDataString,
    handleResetData
  );

  const shureBuySteps = getShureBuySteps(
    fillDataWizard,
    handleChangeDataString,
    handleResetData,
    {
      isNextStep_communicationType,
      isNextStep_timeIntervalTypeCommunication,
      isNextStep_viewRealtorParticipation,
      isNextStep_timeIntervalTypeView,
    }
  );

  const shureSwapSteps = getShureSwapSteps(
    fillDataWizard,
    dataFeatures,
    dataFormEstimate,
    handleChangeDataObject,
    handleChangeDataNumber,
    handleChangeDataString,
    handleResetData,
    {
      isNextStep_estimate,
      isNextStep_dataFeatures,
      isNextStep_communicationType,
      isNextStep_timeIntervalTypeView,
      isNextStep_viewRealtorParticipation,
      isNextStep_timeIntervalTypeCommunication,
    }
  );

  const wishSteps = getWishSteps(
    fillDataWizard,
    handleChangeDataNumber,
    handleChangeDataBoolean,
    handleChangeDataString,
    handleResetData,
    {
      isNextStepDisabled_selectedPolygonIds,
      isNextStepDisabled_selectedTypeIds,
      isNextStepDisabled_finance,
      isNextStepDisabled_aim,
      isTriplePrevSteps,
    }
  );

  const wishBuySteps = getWishBuySteps(
    fillDataWizard,
    handleChangeDataString,
    handleResetData,
    {
      isNextStep_communicationType,
      isNextStep_timeIntervalTypeView,
      isNextStep_viewRealtorParticipation,
      isNextStep_timeIntervalTypeCommunication,
    }
  );

  const wishSwapSteps = getWishSwapSteps(
    fillDataWizard,
    dataFeatures,
    dataFormEstimate,
    handleChangeDataObject,
    handleChangeDataNumber,
    handleChangeDataString,
    handleResetData,
    {
      isNextStep_estimate,
      isNextStep_dataFeatures,
      isNextStep_communicationType,
      isNextStep_timeIntervalTypeView,
      isNextStep_viewRealtorParticipation,
      isNextStep_timeIntervalTypeCommunication,
    }
  );

  const selectionByOwnAppSteps = getSelectionByOwnAppSteps(
    fillDataWizard,
    dataFormEstimate,
    handleChangeDataObject,
    handleChangeDataNumber,
    handleResetData,
    {
      isNextStep_estimateWithoutApartment:
        isNextStep_estimateWithoutApartment_b,
      isNextStepDisabled_selectedFacilityTypeOwnFacility,
    }
  );

  const selectionByOwnAppSwapSteps = getSelectionByOwnAppSwapSteps(
    fillDataWizard,
    dataFeatures,
    handleChangeDataObject,
    handleChangeDataNumber,
    handleChangeDataString,
    handleChangeDataBoolean,
    handleResetData,
    {
      isNextStep_dataFeatures,
      isNextStepDisabled_aim,
      isNextStepDisabled_finance,
      isNextStep_communicationType,
      isNextStep_timeIntervalTypeView,
      isNextStepDisabled_selectedTypeIds,
      isNextStepDisabled_apartmentNumber,
      isNextStep_viewRealtorParticipation,
      isNextStepDisabled_selectedPolygonIds,
      isNextStep_timeIntervalTypeCommunication,
      isDoublePrevSteps,
      isTriplePrevSteps,
    }
  );

  const selectionByOwnAppBuySteps = getSelectionByOwnAppBuySteps(
    fillDataWizard,
    handleChangeDataNumber,
    handleChangeDataString,
    handleChangeDataBoolean,
    handleResetData,
    {
      isNextStepDisabled_aim,
      isNextStepDisabled_finance,
      isNextStep_communicationType,
      isNextStep_timeIntervalTypeView,
      isNextStepDisabled_selectedTypeIds,
      isNextStep_viewRealtorParticipation,
      isNextStepDisabled_selectedPolygonIds,
      isNextStep_timeIntervalTypeCommunication,
      isDoublePrevSteps,
      isTriplePrevSteps,
    }
  );

  const allSteps = [
    ...base,
    ...shureSteps,
    ...shureBuySteps,
    ...shureSwapSteps,
    ...wishSteps,
    ...wishBuySteps,
    ...wishSwapSteps,
    ...selectionByOwnAppSteps,
    ...selectionByOwnAppSwapSteps,
    ...selectionByOwnAppBuySteps,
  ];

  const previousStep = useRef<number>(0);

  return (
    <Wizard
      wrapper={
        <div className={styles.buyWizard}>
          <div className={styles.buyWizard__container}></div>
        </div>
      }
    >
      {allSteps.map((steps, index) => {
        return (
          <AnimatedStep key={index} previousStep={previousStep}>
            <div
              className={clsx(
                styles.buyWizard__card,
                IS_PADDING_NARROW && styles.buyWizard__card_paddingNarrow
              )}
            >
              <div className={styles.buyWizard__title}>Купить жилье</div>
              <div className={styles.buyWizard__wizard}>{steps}</div>
            </div>
          </AnimatedStep>
        );
      })}
    </Wizard>
  );
};

export default BuyWizard;
